/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Typography2, breakpoints } from '@coursera/cds-core';
import { CheckIcon } from '@coursera/cds-icons';
import { Dialog } from '@coursera/event-pulse/react';

import PrePurchaseUpsellCourseraPlusEnrollButton from 'bundles/coursera-plus-common/components/elevateLTV/prePurchaseUpsell/PrePurchaseUpsellCourseraPlusEnrollButton';
import { useGetCourseraPlusProductPricesAndConfig } from 'bundles/coursera-plus-common/utils/prePurchaseUpsellUtils';
import usePageData from 'bundles/enroll-modal/data/usePageData';
import { getEnrollModalName } from 'bundles/enroll-modal/utils/eventingUtils';
import ReactPriceDisplay from 'bundles/ownable-products-common/components/ReactPriceDisplay';
import { ProfessionalCertificateS12n } from 'bundles/s12n-common/constants/s12nProductVariants';

import _t from 'i18n!nls/enroll';

const styles = {
  subtitle: css`
    margin-bottom: var(--cds-spacing-300);
  `,
  row: css`
    display: flex;
    margin-bottom: var(--cds-spacing-200);
    gap: var(--cds-spacing-100);
  `,
  checkIcon: css`
    flex-shrink: 0;
    margin-top: -2px;
  `,
  valuePropHeader: css`
    margin-bottom: var(--cds-spacing-50);
  `,
  button: css`
    ${breakpoints.down('xs')} {
      width: 100%;
      max-width: 100%;
    }
  `,
};

type PropsFromCaller = {
  s12nId: string;
  courseId?: string;
  onClose: () => void;
};

/**
 * Enroll modal for s12ns that are only available through Coursera Plus (see `isCourseraPlusOnlyS12n`). Shown to
 * learners who can subscribe to Coursera Plus, in place of the s12n subscription / purchase modals.
 */
const CourseraPlusOnlyS12nEnrollModal = ({ s12nId, courseId, onClose }: PropsFromCaller) => {
  const { enrollmentAvailableChoices, s12n } = usePageData();
  const hasCourseraPlusFreeTrial = enrollmentAvailableChoices.canSubscribeToCourseraPlusFreeTrial;
  const eventingModalName = getEnrollModalName({ isUsingPrePurchaseUpsellFlow: true })!;

  const { courseraPlusMonthlyProductPrice, courseraPlusMonthlyProductItemId } =
    useGetCourseraPlusProductPricesAndConfig({ hasCourseraPlusFreeTrial });

  if (!courseraPlusMonthlyProductPrice || !courseraPlusMonthlyProductItemId) {
    return null;
  }

  const { HeadingGroup, Content, Actions } = Dialog;

  const productType =
    s12n?.productVariant === ProfessionalCertificateS12n ? _t('Professional Certificate') : _t('Specialization');

  const price = (
    <ReactPriceDisplay
      currency={courseraPlusMonthlyProductPrice.currencyCode}
      value={Number(courseraPlusMonthlyProductPrice.value)}
      hideCurrencyCode={true}
    />
  );

  const valueProps = [
    {
      header: _t('Unlimited Access to all courses in this #{productType}.', { productType }),
      subheader: _t('Watch lectures, try assignments, participate in discussion forums, and more.'),
    },
    {
      header: _t('Cancel anytime.'),
      subheader: hasCourseraPlusFreeTrial
        ? _t("No penalties - simply cancel before the trial ends if it's not right for you.")
        : _t('No penalties - simply cancel if it is not right for you.'),
    },
    {
      header: (
        <FormattedMessage
          message={
            hasCourseraPlusFreeTrial ? _t('{price}/month to continue learning after trial ends.') : _t('{price}/month.')
          }
          price={price}
        />
      ),
      subheader: _t('Enjoy the freedom to learn at your own pace.'),
    },
    {
      header: hasCourseraPlusFreeTrial
        ? _t('Certificate when you complete after your trial ends.')
        : _t('Certificate when you complete.'),
      subheader: _t('Share on your resume, Linkedin, and CV.'),
    },
  ];

  return (
    <Dialog
      data-testid="coursera-plus-only-s12n-enroll-modal"
      aria-describedby="dialog-content"
      open
      variant="standard"
      width="medium"
      sticky="actions"
      onClose={onClose}
      trackingData={{ modal: { name: eventingModalName } }}
    >
      <HeadingGroup>
        {hasCourseraPlusFreeTrial ? _t('Start your 7-day free trial') : _t('Subscribe to Coursera Plus')}
      </HeadingGroup>
      <Content id="dialog-content">
        <Typography2 variant="subtitleMedium" component="p" css={styles.subtitle}>
          {_t(
            'This content is included as part of the Google Collection within a Coursera Plus subscription, which gives you access to this content and thousands more courses and Specializations.'
          )}
        </Typography2>
        {valueProps.map(({ header, subheader }) => (
          <div css={styles.row} key={subheader}>
            <CheckIcon color="interactive" size="small" css={styles.checkIcon} />
            <div>
              <Typography2 variant="subtitleMedium" component="div" css={styles.valuePropHeader}>
                {header}
              </Typography2>
              <Typography2 component="p">{subheader}</Typography2>
            </div>
          </div>
        ))}
      </Content>
      <Actions>
        <PrePurchaseUpsellCourseraPlusEnrollButton
          trackingName="coursera_plus_only_s12n_enroll_cta"
          variant="primary"
          css={styles.button}
          productItemId={courseraPlusMonthlyProductItemId}
          courseId={courseId}
          s12nId={s12nId}
          isPrePurchaseUpsellAllPlansShown={false}
        >
          {hasCourseraPlusFreeTrial ? _t('Start free trial') : _t('Subscribe')}
        </PrePurchaseUpsellCourseraPlusEnrollButton>
      </Actions>
    </Dialog>
  );
};

export default CourseraPlusOnlyS12nEnrollModal;
